import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  BookOpen,
  CalendarDays,
  Check,
  CheckCircle2,
  Clock,
  Download,
  FileText,
  Globe2,
  KeyRound,
  LoaderCircle,
  LogOut,
  Mail,
  Menu,
  Minus,
  Phone,
  Share2,
  UploadCloud,
  UserRound,
  X,
} from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';

import {
  api,
  apiBaseUrl,
  ApiError,
  uploadAvatar,
  type ApplicantApplication,
  type ApplicantProfile,
  type ApplicantNotice,
  type ApplicantWorkflowStage,
  type CustomStatus,
  type Appointment,
  type DocumentRequest,
  type SchedulingHandoff,
  type SharedResource,
  type Session,
} from '../lib/api';
import { Avatar } from '../shared/Avatar';
import { CalcomEmbed } from '../shared/CalcomEmbed';
import { ChangePasswordModal } from '../shared/ChangePasswordModal';
import { SearchableSelect } from '../shared/SearchableSelect';
import { StatusBadge } from '../shared/StatusBadge';
import {
  appointmentStatusTone,
  documentRequestStatusTone,
  documentUploadStatusTone,
  stageStatusTone,
} from '../shared/statusTones';

const APPOINTMENT_STATUS_LABELS: Record<string, { en: string; es: string }> = {
  pending_confirmation: { en: 'Pending confirmation', es: 'Pendiente de confirmación' },
  confirmed: { en: 'Confirmed', es: 'Confirmado' },
  rescheduled: { en: 'Rescheduled', es: 'Reprogramado' },
  cancelled: { en: 'Cancelled', es: 'Cancelado' },
  rejected: { en: 'Not confirmed', es: 'No confirmado' },
  completed: { en: 'Completed', es: 'Completado' },
  no_show: { en: 'Missed', es: 'No asistió' },
};
const appointmentStatusLabel = (status: string, language: 'en' | 'es') =>
  APPOINTMENT_STATUS_LABELS[status]?.[language] ?? status.replaceAll('_', ' ');

const DOCUMENT_UPLOAD_STATUS_LABELS: Record<string, { en: string; es: string }> = {
  quarantined: { en: 'Processing', es: 'Procesando' },
  available: { en: 'Uploaded', es: 'Subido' },
  accepted: { en: 'Accepted', es: 'Aceptado' },
  rejected: { en: 'Rejected', es: 'Rechazado' },
  superseded: { en: 'Replaced', es: 'Reemplazado' },
  withdrawn: { en: 'Withdrawn', es: 'Retirado' },
};
const documentUploadStatusLabel = (status: string, language: 'en' | 'es') =>
  DOCUMENT_UPLOAD_STATUS_LABELS[status]?.[language] ?? status.replaceAll('_', ' ');

/**
 * `Intl.DateTimeFormat` rejects combining `dateStyle`/`timeStyle` with `timeZoneName`, so the
 * timezone abbreviation is computed separately and appended to the date/time string.
 */
const formatAppointmentTime = (startsAt: string, timezone: string, language: 'en' | 'es') => {
  const locale = language === 'es' ? 'es-US' : 'en-US';
  const date = new Date(startsAt);
  const dateTime = date.toLocaleString(locale, { dateStyle: 'medium', timeStyle: 'short', timeZone: timezone });
  const tzPart = new Intl.DateTimeFormat(locale, { timeZone: timezone, timeZoneName: 'short' })
    .formatToParts(date)
    .find((part) => part.type === 'timeZoneName');
  return tzPart ? `${dateTime} ${tzPart.value}` : dateTime;
};

type Copy = Record<string, { en: string; es: string }>;
const copy: Copy = {
  dashboard: { en: 'Dashboard', es: 'Panel' },
  profile: { en: 'Profile', es: 'Perfil' },
  welcome: { en: 'Welcome back', es: 'Bienvenido de nuevo' },
  application: { en: 'Credential application', es: 'Solicitud de credencial' },
  currentStatus: { en: 'Current status', es: 'Estado actual' },
  yourStatus: { en: 'Your application status', es: 'Estado de su solicitud' },
  nextStep: { en: 'Your next step', es: 'Su próximo paso' },
  youAreHere: { en: 'You are here', es: 'Está aquí' },
  scheduleCta: { en: 'Schedule now', es: 'Programar ahora' },
  notices: { en: 'Important notices', es: 'Avisos importantes' },
  documents: { en: 'Document requests', es: 'Solicitudes de documentos' },
  scheduling: { en: 'Scheduling', es: 'Programación' },
  noNotices: {
    en: 'There are no important notices right now.',
    es: 'No hay avisos importantes en este momento.',
  },
  documentsHint: {
    en: 'Document requests will appear here when staff sends them.',
    es: 'Las solicitudes de documentos aparecerán aquí cuando el personal las envíe.',
  },
  scheduleHint: {
    en: 'Eligible interview or exam scheduling will appear here. Availability is provided securely through Cal.com.',
    es: 'La programación elegible de entrevistas o exámenes aparecerá aquí. La disponibilidad se proporciona de forma segura mediante Cal.com.',
  },
  language: { en: 'Preferred language', es: 'Idioma preferido' },
  email: { en: 'Email', es: 'Correo electrónico' },
  phone: { en: 'Phone', es: 'Teléfono' },
  saved: {
    en: 'Your language preference was updated.',
    es: 'Su preferencia de idioma se actualizó.',
  },
  signOut: { en: 'Sign out', es: 'Cerrar sesión' },
  changePassword: { en: 'Change password', es: 'Cambiar contraseña' },
  retry: { en: 'Try again', es: 'Intentar de nuevo' },
  loading: { en: 'Loading your portal', es: 'Cargando su portal' },
  upload: { en: 'Upload document', es: 'Subir documento' },
  uploading: { en: 'Uploading…', es: 'Subiendo…' },
  attachFiles: { en: 'Attach files', es: 'Adjuntar archivos' },
  submitDocuments: { en: 'Submit', es: 'Enviar' },
  remove: { en: 'Remove', es: 'Quitar' },
  cancel: { en: 'Cancel', es: 'Cancelar' },
  confirmRemoveStaged: {
    en: "Remove this file before it's submitted?",
    es: '¿Quitar este archivo antes de enviarlo?',
  },
  staffNote: { en: 'Note from staff', es: 'Nota del personal' },
  credentialPortal: {
    en: 'Credential Application Tracking',
    es: 'Seguimiento de solicitud de credenciales',
  },
  applicantNav: { en: 'Applicant navigation', es: 'Navegación del solicitante' },
  openNav: { en: 'Open navigation', es: 'Abrir navegación' },
  closeNav: { en: 'Close navigation', es: 'Cerrar navegación' },
  languageHelp: {
    en: "We'll use this language for your portal, and for emails and texts we send you.",
    es: 'Usaremos este idioma para su portal, y para los correos y mensajes de texto que le enviemos.',
  },
  manageBooking: { en: 'Manage booking', es: 'Administrar reservación' },
  reschedule: { en: 'Reschedule', es: 'Reprogramar' },
  rescheduleHeading: { en: 'Reschedule your appointment', es: 'Reprogramar su cita' },
  bookNewAppointment: { en: 'Book new appointment', es: 'Programar nueva cita' },
  backToScheduling: { en: 'Back to scheduling', es: 'Volver a la programación' },
  cancelAppointment: { en: 'Cancel appointment', es: 'Cancelar cita' },
  cancelAppointmentWarning: {
    en: 'This cancels your appointment. You may then be able to book a new one.',
    es: 'Esto cancelará su cita. Es posible que luego pueda programar una nueva.',
  },
  cancelReasonLabel: {
    en: 'Reason for cancelling (required)',
    es: 'Motivo de la cancelación (obligatorio)',
  },
  confirmCancellation: { en: 'Confirm cancellation', es: 'Confirmar cancelación' },
  cancelling: { en: 'Cancelling…', es: 'Cancelando…' },
  keepAppointment: { en: 'Keep appointment', es: 'Mantener cita' },
  appointmentCancelled: { en: 'Your appointment was cancelled.', es: 'Su cita fue cancelada.' },
  troubleLoading: {
    en: 'Trouble loading? Open in a new tab',
    es: '¿Problemas para cargar? Abrir en una pestaña nueva',
  },
  contactInfo: { en: 'Contact information', es: 'Información de contacto' },
  contactOffice: {
    en: 'Contact the district office to update this information.',
    es: 'Comuníquese con la oficina del distrito para actualizar esta información.',
  },
  preferences: { en: 'Preferences', es: 'Preferencias' },
  allSetBody: {
    en: "You're all set for now. Check the important notices below for any updates.",
    es: 'Por ahora no necesita hacer nada. Revise los avisos importantes a continuación por si hay novedades.',
  },
  applicationComplete: {
    en: 'Your application is complete. Check the important notices below for any updates.',
    es: 'Su solicitud está completa. Revise los avisos importantes a continuación por si hay novedades.',
  },
  notApplicable: { en: 'Not applicable', es: 'No aplica' },
  customStatusLabel: { en: 'Additional status', es: 'Estado adicional' },
  statusComplete: { en: 'Complete', es: 'Completo' },
  statusPending: { en: 'Pending', es: 'Pendiente' },
  appointments: { en: 'Your appointments', es: 'Sus citas' },
  scheduleNext: { en: 'Schedule your next appointment', es: 'Programe su próxima cita' },
  sharedResources: { en: 'Shared resources', es: 'Recursos compartidos' },
  sharedResourcesHint: {
    en: 'Files your reviewer shares with you will appear here.',
    es: 'Los archivos que su revisor comparta con usted aparecerán aquí.',
  },
  uploadPhoto: { en: 'Upload profile photo', es: 'Subir foto de perfil' },
  photoUpdated: { en: 'Profile photo updated.', es: 'Foto de perfil actualizada.' },
};

const text = (key: keyof typeof copy, language: 'en' | 'es') => copy[key][language];

/**
 * Bilingual, code-keyed error copy for the applicant portal. The backend only ever writes
 * English messages, so English can usually trust `error.message` directly (see the fallback
 * below) -- but Spanish has nothing to fall back to, so every error code an applicant can
 * realistically hit needs its own hand-written Spanish sentence here, with a matching English
 * version alongside it so both languages stay in sync in tone and terminology.
 */
const ERROR_COPY: Record<string, { en: string; es: string }> = {
  NETWORK_ERROR: {
    en: "We couldn't connect to the server. Check your connection and try again.",
    es: 'No pudimos conectar con el servidor. Verifique su conexión e inténtelo de nuevo.',
  },
  UNAUTHORIZED: {
    en: 'Your session has expired. Please sign in again to continue.',
    es: 'Su sesión ha expirado. Inicie sesión de nuevo para continuar.',
  },
  CSRF_INVALID: {
    en: "We couldn't verify that action. Please refresh the page and try again.",
    es: 'No pudimos verificar esa acción. Actualice la página e inténtelo de nuevo.',
  },
  FORBIDDEN: {
    en: "You don't have permission to do that.",
    es: 'No tiene permiso para hacer esto.',
  },
  NOT_FOUND: {
    en: "We couldn't find what you're looking for.",
    es: 'No pudimos encontrar lo que buscaba.',
  },
  CONFLICT: {
    en: 'This action is no longer available. Please refresh the page and try again.',
    es: 'Esta acción ya no está disponible. Actualice la página e inténtelo de nuevo.',
  },
  GONE: {
    en: 'This link has expired or was already used. Please request a new one.',
    es: 'Este enlace venció o ya fue utilizado. Solicite uno nuevo.',
  },
  VALIDATION_ERROR: {
    en: 'Please review the highlighted fields and correct the information before continuing.',
    es: 'Por favor revise los campos indicados y corrija la información antes de continuar.',
  },
  UPLOAD_FAILED: {
    en: "We couldn't upload your document. Please try again.",
    es: 'No pudimos subir su documento. Inténtelo de nuevo.',
  },
  UPLOAD_VERIFICATION_FAILED: {
    en: "We couldn't verify your upload. Please try uploading the file again.",
    es: 'No pudimos verificar su archivo. Intente subirlo de nuevo.',
  },
  DOCUMENT_UNAVAILABLE: {
    en: 'This document is not available to download yet.',
    es: 'Este documento todavía no está disponible para descargar.',
  },
  RATE_LIMITED: {
    en: 'Too many attempts. Please wait a moment and try again.',
    es: 'Demasiados intentos. Espere un momento e inténtelo de nuevo.',
  },
  INTEGRATION_UNAVAILABLE: {
    en: 'Scheduling is temporarily unavailable. Please try again later or contact the district office.',
    es: 'La programación no está disponible en este momento. Inténtelo más tarde o comuníquese con la oficina del distrito.',
  },
};
const DEFAULT_ERROR = {
  en: "We couldn't complete that action. Please try again.",
  es: 'No pudimos completar esta acción. Inténtelo de nuevo.',
};
const errorMessage = (error: unknown, language: 'en' | 'es'): string => {
  const code = error instanceof ApiError ? error.code : undefined;
  const mapped = code ? ERROR_COPY[code] : undefined;
  if (mapped) return mapped[language];
  if (error instanceof ApiError && language === 'en' && error.message) return error.message;
  return DEFAULT_ERROR[language];
};

function CardSkeleton() {
  return (
    <div className="animate-pulse rounded-card border border-border bg-surface p-5 motion-reduce:animate-none">
      <div className="h-4 w-28 rounded bg-surface-muted" />
      <div className="mt-4 h-8 w-3/4 rounded bg-surface-muted" />
      <div className="mt-3 h-4 w-full rounded bg-surface-muted" />
    </div>
  );
}

export function ApplicantPortal({ session }: { session: Session }) {
  const [mobileNav, setMobileNav] = useState(false);
  const [changePasswordOpen, setChangePasswordOpen] = useState(false);
  const [savingLanguage, setSavingLanguage] = useState(false);
  const changePasswordTriggerRef = useRef<HTMLButtonElement>(null);
  const [view, setView] = useState<'dashboard' | 'scheduling' | 'profile'>('dashboard');
  const queryClient = useQueryClient();
  const profile = useQuery({
    queryKey: ['my-profile'],
    queryFn: () => api<ApplicantProfile>('/me/applicant-profile'),
  });
  const language = profile.data?.preferredLanguage ?? 'en';
  const application = useQuery({
    queryKey: ['my-application'],
    queryFn: () => api<ApplicantApplication>('/me/application'),
  });
  const workflowStages = useQuery({
    queryKey: ['my-workflow-stages', language],
    queryFn: () =>
      api<{ data: ApplicantWorkflowStage[]; customStatus: CustomStatus | null }>('/me/workflow-stages'),
    enabled: !!profile.data,
  });
  const notices = useQuery({
    queryKey: ['my-notices', language],
    queryFn: () => api<{ data: ApplicantNotice[] }>('/me/notices?pageSize=4'),
    enabled: !!profile.data,
  });
  const documents = useQuery({
    queryKey: ['my-document-requests', language],
    queryFn: () => api<{ data: DocumentRequest[] }>('/me/document-requests'),
    enabled: !!profile.data,
  });
  const sharedResources = useQuery({
    queryKey: ['my-shared-resources'],
    queryFn: () => api<{ data: SharedResource[] }>('/me/shared-resources'),
    enabled: !!profile.data,
  });
  const appointments = useQuery({
    queryKey: ['my-appointments'],
    queryFn: () => api<{ data: Appointment[] }>('/me/appointments'),
    enabled: !!profile.data,
  });
  const scheduling = useQuery({
    queryKey: ['my-scheduling'],
    queryFn: () => api<SchedulingHandoff>('/me/scheduling'),
    enabled: !!profile.data,
    retry: false,
  });
  const chooseView = (next: typeof view) => {
    setView(next);
    setMobileNav(false);
  };
  const updateLanguage = async (preferredLanguage: 'en' | 'es') => {
    setSavingLanguage(true);
    try {
      await api<ApplicantProfile>('/me/applicant-profile', {
        method: 'PATCH',
        body: JSON.stringify({ preferredLanguage }),
      });
      await queryClient.invalidateQueries({ queryKey: ['my-profile'] });
      await queryClient.invalidateQueries({ queryKey: ['my-application'] });
      await queryClient.invalidateQueries({ queryKey: ['my-workflow-stages'] });
      await queryClient.invalidateQueries({ queryKey: ['my-notices'] });
      toast.success(text('saved', preferredLanguage));
    } catch (error) {
      toast.error(errorMessage(error, language));
    } finally {
      setSavingLanguage(false);
    }
  };
  const logout = async () => {
    try {
      await api('/auth/logout', { method: 'POST' });
      window.location.reload();
    } catch (error) {
      toast.error(errorMessage(error, language));
    }
  };
  const nav = [
    ['dashboard', text('dashboard', language), CheckCircle2],
    ['scheduling', text('scheduling', language), CalendarDays],
    ['profile', text('profile', language), UserRound],
  ] as const;
  const ViewIcon = nav.find(([id]) => id === view)?.[2];
  return (
    <main className="min-h-screen bg-canvas text-ink lg:flex">
      <aside
        id="applicant-navigation"
        className={`schd-sidebar flex-col border-b border-border bg-burgundy-dark text-white lg:sticky lg:top-0 lg:h-screen lg:w-72 lg:self-start lg:overflow-y-auto lg:border-b-0 lg:border-r ${mobileNav ? 'flex' : 'hidden lg:flex'}`}
      >
        <div className="p-6">
          <img
            src="/brand/schd-logo-white.png"
            alt="South Central Hispanic District"
            className="h-auto w-44 max-w-full"
          />
          <p className="mt-2 text-sm text-white/70">{text('credentialPortal', language)}</p>
        </div>
        <nav
          className="flex flex-col gap-1 border-t border-white/15 px-3 py-4"
          aria-label={text('applicantNav', language)}
        >
          {nav.map(([id, label, Icon]) => (
            <button
              key={id}
              onClick={() => chooseView(id)}
              aria-current={view === id ? 'page' : undefined}
              className={`flex min-h-11 w-full items-center gap-3 rounded-control border-l-[3px] pl-3 pr-3 text-left text-[0.95rem] ${
                view === id
                  ? 'border-l-gold bg-white/15 font-semibold text-white'
                  : 'border-l-transparent text-white/85 hover:bg-white/10 hover:text-white'
              }`}
            >
              <Icon className="size-5 shrink-0" aria-hidden="true" />
              {label}
            </button>
          ))}
        </nav>
        <div className="mt-auto border-t border-white/15 px-4 py-4">
          <label className="flex items-center gap-2 text-xs font-semibold tracking-wide text-white/70 uppercase" htmlFor="portal-language">
            <Globe2 className="size-4" aria-hidden="true" />
            {text('language', language)}
          </label>
          <div className="mt-2">
            <SearchableSelect
              id="portal-language"
              value={language}
              disabled={profile.isLoading || savingLanguage}
              onChange={(value) => void updateLanguage(value as 'en' | 'es')}
              options={[
                { value: 'en', label: 'English' },
                { value: 'es', label: 'Español' },
              ]}
            />
          </div>
          <div className="mt-4 flex flex-col gap-1 border-t border-white/15 pt-4">
            <button
              onClick={() => setChangePasswordOpen(true)}
              ref={changePasswordTriggerRef}
              className="-mx-2 flex min-h-11 items-center gap-2 rounded-control px-2 text-sm text-white/80 hover:bg-white/10 hover:text-white"
            >
              <KeyRound className="size-4 shrink-0" aria-hidden="true" />
              {text('changePassword', language)}
            </button>
            <button
              onClick={() => void logout()}
              className="-mx-2 flex min-h-11 items-center gap-2 rounded-control px-2 text-sm text-white/80 hover:bg-white/10 hover:text-white"
            >
              <LogOut className="size-4 shrink-0" aria-hidden="true" />
              {text('signOut', language)}
            </button>
          </div>
        </div>
      </aside>
      <section className="min-w-0 flex-1 px-4 pb-4 pt-10 sm:p-6 lg:p-9">
        <header className="mx-auto mb-7 flex max-w-3xl items-center gap-3">
          <button
            className="grid size-11 shrink-0 place-items-center rounded-control border border-border bg-surface lg:hidden"
            aria-label={mobileNav ? text('closeNav', language) : text('openNav', language)}
            aria-expanded={mobileNav}
            aria-controls="applicant-navigation"
            onClick={() => setMobileNav(!mobileNav)}
          >
            {mobileNav ? <X /> : <Menu />}
          </button>
          <div>
            <div className="flex items-center gap-2">
              {ViewIcon && <ViewIcon className="size-5 shrink-0 text-burgundy" aria-hidden="true" />}
              <h1 className="font-display text-2xl sm:text-3xl">
                {view === 'dashboard'
                  ? `${text('welcome', language)}, ${profile.data?.name.firstName || session.user.name}`
                  : text(view, language)}
              </h1>
            </div>
            {view === 'dashboard' && (
              <p className="mt-1 text-ink/65">{text('application', language)}</p>
            )}
          </div>
        </header>
        {profile.isLoading ? (
          <div aria-live="polite">
            <span className="sr-only">{text('loading', language)}</span>
            <div className="mx-auto grid max-w-3xl gap-4 lg:grid-cols-2">
              <CardSkeleton />
              <CardSkeleton />
            </div>
          </div>
        ) : profile.isError ? (
          <div className="mx-auto max-w-3xl">
            <ErrorState
              error={profile.error}
              language={language}
              retry={() => void profile.refetch()}
            />
          </div>
        ) : (
          <div key={view} className="schd-tab-fade-in">
            {view === 'dashboard' ? (
              <Dashboard
                application={application}
                workflowStages={workflowStages}
                notices={notices}
                documents={documents}
                sharedResources={sharedResources}
                scheduling={scheduling}
                language={language}
                openScheduling={() => chooseView('scheduling')}
              />
            ) : view === 'scheduling' ? (
              <Scheduling appointments={appointments} scheduling={scheduling} language={language} />
            ) : (
              <Profile
                profile={profile.data!}
                language={language}
                updateLanguage={updateLanguage}
                savingLanguage={savingLanguage}
              />
            )}
          </div>
        )}
      </section>
      {changePasswordOpen && (
        <ChangePasswordModal
          onClose={() => {
            setChangePasswordOpen(false);
            changePasswordTriggerRef.current?.focus();
          }}
          language={language}
        />
      )}
    </main>
  );
}

function Dashboard({
  application,
  workflowStages,
  notices,
  documents,
  sharedResources,
  scheduling,
  language,
  openScheduling,
}: {
  application: ReturnType<typeof useQuery<ApplicantApplication>>;
  workflowStages: ReturnType<
    typeof useQuery<{ data: ApplicantWorkflowStage[]; customStatus: CustomStatus | null }>
  >;
  notices: ReturnType<typeof useQuery<{ data: ApplicantNotice[] }>>;
  documents: ReturnType<typeof useQuery<{ data: DocumentRequest[] }>>;
  sharedResources: ReturnType<typeof useQuery<{ data: SharedResource[] }>>;
  scheduling: ReturnType<typeof useQuery<SchedulingHandoff>>;
  language: 'en' | 'es';
  openScheduling: () => void;
}) {
  const applicationData = application.data;
  const stages = workflowStages.data?.data ?? [];
  const customStatus = workflowStages.data?.customStatus ?? null;
  const hasScheduling = !!scheduling.data?.eventMappings?.length;
  const stepsResolved = stages.filter((s) => s.status !== 'pending').length;
  return (
    <div className="mx-auto max-w-3xl space-y-6">
      <section className="rounded-card border border-border bg-surface p-5">
        <h2 className="text-lg font-semibold">{text('notices', language)}</h2>
        {notices.isLoading ? (
          <div className="mt-4">
            <CardSkeleton />
          </div>
        ) : notices.isError ? (
          <ErrorState
            error={notices.error}
            language={language}
            retry={() => void notices.refetch()}
          />
        ) : notices.data?.data.length ? (
          <ul className="mt-3 divide-y divide-border">
            {notices.data.data.map((notice) => (
              <li key={notice.id} className="py-3">
                <strong>{notice.title}</strong>
                <p className="mt-1 text-sm leading-6 text-ink/70">{notice.body}</p>
              </li>
            ))}
          </ul>
        ) : (
          <Empty icon={CheckCircle2} text={text('noNotices', language)} />
        )}
      </section>
      <section>
        <h2 className="text-lg font-semibold">{text('documents', language)}</h2>
        <div className="mt-3">
          <Documents requests={documents} language={language} />
        </div>
      </section>
      <section>
        <h2 className="text-lg font-semibold">{text('sharedResources', language)}</h2>
        <div className="mt-3">
          <SharedResources resources={sharedResources} language={language} />
        </div>
      </section>
      {application.isLoading ? (
        <CardSkeleton />
      ) : application.isError ? (
        <ErrorState
          error={application.error}
          language={language}
          retry={() => void application.refetch()}
        />
      ) : applicationData ? (
        <section className="rounded-card border border-border bg-surface p-5 shadow-card sm:p-6">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <h2 className="text-lg font-semibold sm:text-xl">{text('yourStatus', language)}</h2>
            {stages.length > 0 && (
              <p className="shrink-0 text-sm font-semibold text-burgundy">
                {language === 'es' ? 'Paso' : 'Step'} {stepsResolved}{' '}
                {language === 'es' ? 'de' : 'of'} {stages.length}
              </p>
            )}
          </div>
          {stages.length > 0 && (
            <div
              className="mt-3 h-2 w-full overflow-hidden rounded-full bg-surface-muted"
              role="progressbar"
              aria-valuenow={stepsResolved}
              aria-valuemin={0}
              aria-valuemax={stages.length}
              aria-label={text('yourStatus', language)}
            >
              <div
                className="h-full rounded-full bg-burgundy transition-[width]"
                style={{ width: `${(stepsResolved / stages.length) * 100}%` }}
              />
            </div>
          )}
          {hasScheduling ? (
            <div className="mt-5 rounded-control border-l-4 border-burgundy bg-burgundy-soft p-4">
              <p className="text-xs font-semibold tracking-wide text-burgundy uppercase">
                {text('nextStep', language)}
              </p>
              <p className="mt-1 leading-6 text-ink">
                {language === 'es'
                  ? `Puede programar: ${(scheduling.data?.eventMappings ?? []).map((m) => m.name).join(', ')}.`
                  : `You're ready to schedule: ${(scheduling.data?.eventMappings ?? []).map((m) => m.name).join(', ')}.`}
              </p>
              <button type="button" className="schd-button-primary mt-3" onClick={openScheduling}>
                {text('scheduleCta', language)}
              </button>
            </div>
          ) : (
            <div className="mt-5 flex items-start gap-3 rounded-control bg-green-soft p-4">
              <CheckCircle2 className="mt-0.5 size-5 shrink-0 text-green" aria-hidden="true" />
              <div>
                <p className="text-xs font-semibold tracking-wide text-green uppercase">
                  {text('nextStep', language)}
                </p>
                <p className="mt-1 leading-6 text-ink/80">
                  {text(applicationData?.complete ? 'applicationComplete' : 'allSetBody', language)}
                </p>
              </div>
            </div>
          )}
          {stages.length > 0 ? (
            <ol className="mt-5 space-y-2" aria-label={text('yourStatus', language)}>
              {stages.map((stage, index) => {
                const done = stage.status === 'complete';
                const notApplicable = stage.status === 'not_applicable';
                const current =
                  !applicationData?.complete && stage.id === applicationData?.currentStage.id;
                return (
                  <li
                    key={stage.id}
                    className={`flex items-center gap-3 rounded-control p-3 ${
                      current
                        ? 'border-2 border-burgundy bg-burgundy-soft'
                        : 'border border-transparent bg-surface-muted'
                    } ${notApplicable ? 'opacity-60' : ''}`}
                  >
                    <span
                      className={`grid size-8 shrink-0 place-items-center rounded-full text-xs font-bold ${
                        done || current
                          ? 'bg-burgundy text-white'
                          : 'border border-border bg-surface text-ink/45'
                      }`}
                      aria-hidden="true"
                    >
                      {done ? (
                        <Check className="size-4" />
                      ) : notApplicable ? (
                        <Minus className="size-4" />
                      ) : (
                        index + 1
                      )}
                    </span>
                    <span
                      className={`flex-1 ${current ? 'font-semibold text-ink' : done ? 'text-ink/80' : 'text-ink/50'}`}
                    >
                      {stage.name}
                    </span>
                    {notApplicable ? (
                      <StatusBadge tone={stageStatusTone(stage.status)}>
                        {text('notApplicable', language)}
                      </StatusBadge>
                    ) : (
                      current && (
                        <span className="shrink-0 text-xs font-semibold tracking-wide text-burgundy uppercase">
                          {text('youAreHere', language)}
                        </span>
                      )
                    )}
                  </li>
                );
              })}
            </ol>
          ) : (
            <p className="mt-5 text-ink/70">
              {text('currentStatus', language)}: <strong>{applicationData.currentStage.name}</strong>
            </p>
          )}
          {customStatus && (
            <div
              className={`mt-2 flex items-center gap-3 rounded-control border border-dashed p-3 ${
                customStatus.status === 'not_applicable'
                  ? 'border-border opacity-60'
                  : 'border-burgundy/30 bg-burgundy-soft/40'
              }`}
            >
              <span
                className={`grid size-8 shrink-0 place-items-center rounded-full text-xs font-bold ${
                  customStatus.status === 'complete'
                    ? 'bg-burgundy text-white'
                    : 'border border-border bg-surface text-ink/45'
                }`}
                aria-hidden="true"
              >
                {customStatus.status === 'complete' ? (
                  <Check className="size-4" />
                ) : customStatus.status === 'not_applicable' ? (
                  <Minus className="size-4" />
                ) : null}
              </span>
              <span className="flex-1">
                <span className="block font-semibold text-ink">{customStatus.name}</span>
                <span className="block text-xs text-ink/55">{text('customStatusLabel', language)}</span>
              </span>
              <StatusBadge tone={stageStatusTone(customStatus.status)}>
                {customStatus.status === 'not_applicable'
                  ? text('notApplicable', language)
                  : customStatus.status === 'complete'
                    ? text('statusComplete', language)
                    : text('statusPending', language)}
              </StatusBadge>
            </div>
          )}
        </section>
      ) : null}
    </div>
  );
}

const CANCELLABLE_APPOINTMENT_STATUSES = ['pending_confirmation', 'confirmed', 'rescheduled'];

function Scheduling({
  appointments,
  scheduling,
  language,
}: {
  appointments: ReturnType<typeof useQuery<{ data: Appointment[] }>>;
  scheduling: ReturnType<typeof useQuery<SchedulingHandoff>>;
  language: 'en' | 'es';
}) {
  const [cancellingId, setCancellingId] = useState<string | null>(null);
  const [cancelReason, setCancelReason] = useState('');
  const [cancelBusy, setCancelBusy] = useState(false);
  const cancelAppointment = async (appointmentId: string) => {
    if (!cancelReason.trim()) return;
    setCancelBusy(true);
    try {
      await api(`/me/appointments/${appointmentId}/cancel`, {
        method: 'POST',
        body: JSON.stringify({ reason: cancelReason.trim() }),
      });
      toast.success(text('appointmentCancelled', language));
      setCancellingId(null);
      setCancelReason('');
      await Promise.all([appointments.refetch(), scheduling.refetch()]);
    } catch (error) {
      toast.error(errorMessage(error, language));
    } finally {
      setCancelBusy(false);
    }
  };
  const existing = appointments.data?.data ?? [];
  const hasAppointments = existing.length > 0;
  // Only an appointment that's still live for its type should block re-offering that type --
  // one that was cancelled or rejected shouldn't stop the applicant from booking it again.
  const bookedTypeIds = new Set(
    existing.filter((a) => !['cancelled', 'rejected'].includes(a.status)).map((a) => a.appointmentTypeId),
  );
  const mappings = scheduling.data?.eventMappings ?? [];
  const bookableMappings = mappings.filter((m) => !bookedTypeIds.has(m.appointmentTypeId));
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const bookable =
    bookableMappings.find((m) => m.appointmentTypeId === selectedId) ?? bookableMappings[0] ?? null;

  // Reschedule reuses this same inline embed rather than opening Cal.com's hosted reschedule
  // page in a new tab -- the embed just needs to be pointed at the appointment's reschedule URL
  // (a plain Cal.com path, same as any event-type booking URL) instead of a bookable type's URL.
  // Keyed by id, not the object itself, so it tracks the freshest row across refetches (e.g. once
  // a completed reschedule lands, or if the appointment is cancelled from elsewhere).
  const [rescheduleTargetId, setRescheduleTargetId] = useState<string | null>(null);
  const rescheduleTarget = existing.find((a) => a.id === rescheduleTargetId) ?? null;
  const rescheduleMapping = useMemo(
    () =>
      rescheduleTarget && rescheduleTarget.bookingUrl
        ? {
            appointmentTypeId: rescheduleTarget.appointmentTypeId,
            name: rescheduleTarget.typeName,
            description: null,
            embedUrl: rescheduleTarget.bookingUrl,
            fallbackUrl: rescheduleTarget.bookingUrl,
          }
        : null,
    [rescheduleTarget],
  );
  const embedSectionRef = useRef<HTMLDivElement>(null);
  // A dedicated counter (rather than watching rescheduleTargetId/selectedId directly) so a row
  // action always scrolls, but switching bookable types from inside the panel itself -- or a
  // data refetch while a reschedule is active -- doesn't.
  const [scrollSignal, setScrollSignal] = useState(0);
  const scrollToEmbed = () => setScrollSignal((n) => n + 1);
  useEffect(() => {
    if (scrollSignal) embedSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, [scrollSignal]);
  const startReschedule = (appointmentId: string) => {
    setRescheduleTargetId(appointmentId);
    scrollToEmbed();
  };
  const startBookingType = (appointmentTypeId: string) => {
    setRescheduleTargetId(null);
    setSelectedId(appointmentTypeId);
    scrollToEmbed();
  };
  return (
    <section className="space-y-10">
      {appointments.isLoading || scheduling.isLoading ? (
        <div className="mx-auto max-w-3xl">
          <CardSkeleton />
        </div>
      ) : (
        <>
          {hasAppointments && (
            <div className="mx-auto max-w-3xl">
              <h2 className="mb-3 text-lg font-semibold">{text('appointments', language)}</h2>
              <ul className="space-y-3">
                {existing.map((item) => {
                  const isLive = CANCELLABLE_APPOINTMENT_STATUSES.includes(item.status);
                  const cancellable = isLive && item.selfServiceCancel;
                  // A cancelled/rejected appointment has nothing to reschedule -- instead, if its
                  // type is still bookable (not blocked by a live appointment of the same type,
                  // and still offered at the applicant's current stage), offer booking a new one.
                  const bookableForType = !isLive
                    ? bookableMappings.find((m) => m.appointmentTypeId === item.appointmentTypeId)
                    : undefined;
                  return (
                    <li
                      key={item.id}
                      className="rounded-control border border-border bg-surface px-4 py-3"
                    >
                      <div className="flex flex-wrap items-center justify-between gap-3">
                        <div className="flex min-w-0 items-start gap-3">
                          <CalendarDays
                            className="mt-0.5 size-5 shrink-0 text-burgundy"
                            aria-hidden="true"
                          />
                          <div className="min-w-0">
                            <p className="font-semibold break-words">{item.typeName}</p>
                            <p className="mt-0.5 flex items-center gap-1.5 text-sm text-ink/65">
                              <Clock className="size-3.5 shrink-0" aria-hidden="true" />
                              {formatAppointmentTime(item.startsAt, item.timezone, language)}
                            </p>
                          </div>
                        </div>
                        <div className="flex shrink-0 items-center gap-3">
                          <StatusBadge tone={appointmentStatusTone(item.status)}>
                            {appointmentStatusLabel(item.status, language)}
                          </StatusBadge>
                          {item.bookingUrl && (
                            <button
                              type="button"
                              className="schd-button-tertiary"
                              onClick={() => startReschedule(item.id)}
                            >
                              {text('reschedule', language)}
                            </button>
                          )}
                          {cancellable && cancellingId !== item.id && (
                            <button
                              type="button"
                              className="schd-button-tertiary"
                              onClick={() => {
                                setCancellingId(item.id);
                                setCancelReason('');
                              }}
                            >
                              <X className="size-4" aria-hidden="true" />
                              {text('cancelAppointment', language)}
                            </button>
                          )}
                          {bookableForType && (
                            <button
                              type="button"
                              className="schd-button-tertiary"
                              onClick={() => startBookingType(item.appointmentTypeId)}
                            >
                              {text('bookNewAppointment', language)}
                            </button>
                          )}
                        </div>
                      </div>
                      {cancellingId === item.id && (
                        <div className="mt-3 rounded-control border border-border bg-surface-muted p-3">
                          <p className="text-sm text-ink/80">
                            {text('cancelAppointmentWarning', language)}
                          </p>
                          <label className="mt-2 block text-sm text-ink/80">
                            {text('cancelReasonLabel', language)}
                            <input
                              required
                              value={cancelReason}
                              onChange={(event) => setCancelReason(event.target.value)}
                              className="mt-1 block min-h-9 w-full rounded-control border border-border bg-surface px-2 font-normal text-ink"
                            />
                          </label>
                          <div className="mt-2 flex flex-wrap items-center gap-2">
                            <button
                              type="button"
                              disabled={cancelBusy || !cancelReason.trim()}
                              className="schd-button-destructive-sm disabled:opacity-50"
                              onClick={() => void cancelAppointment(item.id)}
                            >
                              {cancelBusy ? (
                                <LoaderCircle
                                  className="size-4 animate-spin motion-reduce:animate-none"
                                  aria-hidden="true"
                                />
                              ) : null}
                              {cancelBusy
                                ? text('cancelling', language)
                                : text('confirmCancellation', language)}
                            </button>
                            <button
                              type="button"
                              className="schd-button-secondary-sm"
                              disabled={cancelBusy}
                              onClick={() => setCancellingId(null)}
                            >
                              {text('keepAppointment', language)}
                            </button>
                          </div>
                        </div>
                      )}
                    </li>
                  );
                })}
              </ul>
            </div>
          )}
          {rescheduleMapping ? (
            // Reuses the same inline embed as booking -- just pointed at this appointment's
            // reschedule URL instead of a bookable type's booking URL.
            <div ref={embedSectionRef} className="mx-auto max-w-5xl">
              <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
                <h2 className="text-lg font-semibold">
                  {text('rescheduleHeading', language)} — {rescheduleMapping.name}
                </h2>
                <button
                  type="button"
                  className="schd-link text-sm"
                  onClick={() => setRescheduleTargetId(null)}
                >
                  {text('backToScheduling', language)}
                </button>
              </div>
              <CalcomEmbed mapping={rescheduleMapping} />
              <p className="mt-3 text-center text-sm">
                <a className="schd-link" href={rescheduleMapping.fallbackUrl} target="_blank" rel="noreferrer">
                  {text('troubleLoading', language)}
                </a>
              </p>
            </div>
          ) : scheduling.isError ? (
            <div className="mx-auto max-w-3xl schd-error" role="alert">
              <p>{errorMessage(scheduling.error, language)}</p>
              <button className="schd-button-secondary mt-3" onClick={() => void scheduling.refetch()}>
                {text('retry', language)}
              </button>
            </div>
          ) : bookable ? (
            // Wider than the appointments list above -- Cal.com's month-view calendar switches
            // to a compact side-by-side calendar+times layout once it has enough room, and stays
            // cramped into a tall, scrolling single column when it doesn't.
            <div ref={embedSectionRef} className="mx-auto max-w-5xl">
              {hasAppointments && (
                <h2 className="mb-3 text-lg font-semibold">{text('scheduleNext', language)}</h2>
              )}
              {bookableMappings.length > 1 && (
                <div className="mb-4 flex flex-wrap gap-2">
                  {bookableMappings.map((option) => (
                    <button
                      key={option.appointmentTypeId}
                      type="button"
                      onClick={() => setSelectedId(option.appointmentTypeId)}
                      aria-pressed={bookable.appointmentTypeId === option.appointmentTypeId}
                      className={`min-h-11 rounded-control px-4 text-sm font-semibold ${
                        bookable.appointmentTypeId === option.appointmentTypeId
                          ? 'bg-burgundy text-white'
                          : 'border border-border bg-surface text-ink hover:bg-surface-muted'
                      }`}
                    >
                      {option.name}
                    </button>
                  ))}
                </div>
              )}
              <CalcomEmbed mapping={bookable} />
              <p className="mt-3 text-center text-sm">
                <a className="schd-link" href={bookable.fallbackUrl} target="_blank" rel="noreferrer">
                  {text('troubleLoading', language)}
                </a>
              </p>
            </div>
          ) : !hasAppointments ? (
            <div className="mx-auto max-w-3xl schd-card">
              <Empty icon={CalendarDays} text={text('scheduleHint', language)} />
            </div>
          ) : null}
        </>
      )}
    </section>
  );
}

const documentRequestStatusLabel = (status: string, language: 'en' | 'es') => {
  const labels: Record<string, { en: string; es: string }> = {
    pending: { en: 'Required', es: 'Requerido' },
    uploaded: { en: 'Submitted', es: 'Enviado' },
    under_review: { en: 'Under review', es: 'En revisión' },
    accepted: { en: 'Accepted', es: 'Aceptado' },
    waived: { en: 'Waived', es: 'Exento' },
    replacement_requested: { en: 'Replacement needed', es: 'Se necesita reemplazo' },
    rejected: { en: 'Replacement needed', es: 'Se necesita reemplazo' },
    cancelled: { en: 'Cancelled', es: 'Cancelado' },
    expired: { en: 'Expired', es: 'Vencido' },
  };
  return labels[status]?.[language] ?? status.replaceAll('_', ' ');
};

async function uploadDocumentFile(documentRequestId: string, file: File) {
  const digest = await crypto.subtle.digest('SHA-256', await file.arrayBuffer());
  const sha256 = Array.from(new Uint8Array(digest), (byte) => byte.toString(16).padStart(2, '0')).join(
    '',
  );
  const intent = await api<{
    uploadId: string;
    uploadUrl: string;
    requiredHeaders: Record<string, string>;
  }>('/me/documents/uploads', {
    method: 'POST',
    body: JSON.stringify({
      documentRequestId,
      fileName: file.name,
      contentType: file.type,
      contentLength: file.size,
      sha256,
    }),
  });
  const uploaded = await fetch(intent.uploadUrl, {
    method: 'PUT',
    headers: intent.requiredHeaders,
    body: file,
  });
  if (!uploaded.ok)
    throw new ApiError('UPLOAD_FAILED', "We couldn't upload your document. Please try again.");
  await api(`/me/documents/uploads/${intent.uploadId}/complete`, {
    method: 'POST',
    body: JSON.stringify({ sha256 }),
  });
}

function DocumentRequestCard({
  request,
  language,
  onChanged,
}: {
  request: DocumentRequest;
  language: 'en' | 'es';
  onChanged: () => Promise<unknown>;
}) {
  const [staged, setStaged] = useState<File[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [confirmRemove, setConfirmRemove] = useState<number | null>(null);

  // A request is only open for upload until the applicant submits something. Once status leaves
  // 'pending' it's locked for good -- no attaching more files, no retracting what was sent. If
  // staff need something different, they open a brand new request rather than reopening this one.
  const open = request.status === 'pending';
  // A withdrawn document is gone as far as the applicant is concerned -- keep it in the record for
  // staff, but don't list it here.
  const visibleDocuments = request.documents.filter((document) => document.status !== 'withdrawn');
  const accentClass = (() => {
    const tone = documentRequestStatusTone(request.status);
    if (tone === 'attention') return 'schd-accent-attention';
    if (tone === 'negative') return 'schd-accent-negative';
    return '';
  })();

  const addFiles = (files: FileList | null) => {
    if (!files?.length) return;
    setStaged((prev) => [...prev, ...Array.from(files)]);
  };
  const submit = async () => {
    if (!staged.length) return;
    setSubmitting(true);
    try {
      for (const file of staged) {
        await uploadDocumentFile(request.id, file);
      }
      setStaged([]);
      await onChanged();
      toast.success(
        language === 'es'
          ? 'Documentos recibidos. El personal los revisará.'
          : 'Documents received. Staff will review them.',
      );
    } catch (error) {
      toast.error(errorMessage(error, language));
    } finally {
      setSubmitting(false);
    }
  };
  return (
    <li className={`schd-card ${accentClass}`}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <h3 className="text-lg font-semibold break-words">{request.title}</h3>
          {request.instructions && (
            <p className="mt-1 text-sm leading-6 text-ink/70">{request.instructions}</p>
          )}
          {request.notesToApplicant && (
            <div className="mt-2 rounded-control border-l-4 border-burgundy bg-burgundy-soft p-3">
              <p className="text-xs font-semibold tracking-wide text-burgundy uppercase">
                {text('staffNote', language)}
              </p>
              <p className="mt-1 text-sm leading-6 text-ink">{request.notesToApplicant}</p>
            </div>
          )}
          {request.dueAt && (
            <p className="mt-2 text-sm text-ink/65">
              {language === 'es' ? 'Fecha límite' : 'Due'}:{' '}
              {new Date(request.dueAt).toLocaleDateString(language === 'es' ? 'es-US' : 'en-US', {
                dateStyle: 'medium',
              })}
            </p>
          )}
        </div>
        <StatusBadge tone={documentRequestStatusTone(request.status)}>
          {documentRequestStatusLabel(request.status, language)}
        </StatusBadge>
      </div>
      {visibleDocuments.length > 0 && (
        <ul className="mt-4 divide-y divide-border rounded-control bg-surface-muted px-3">
          {visibleDocuments.map((document) => (
            <li
              key={document.id}
              className="flex flex-wrap items-center justify-between gap-3 py-3 text-sm"
            >
              <span className="flex min-w-0 items-center gap-2">
                <FileText className="size-4 shrink-0 text-ink/40" aria-hidden="true" />
                <span className="min-w-0 break-all">{document.fileName}</span>
              </span>
              <span className="flex shrink-0 flex-wrap items-center gap-2">
                <StatusBadge tone={documentUploadStatusTone(document.status)}>
                  {documentUploadStatusLabel(document.status, language)}
                </StatusBadge>
                {['available', 'accepted'].includes(document.status) && (
                  <a
                    className="schd-button-tertiary"
                    href={`${apiBaseUrl}/me/documents/${document.id}/download`}
                  >
                    <Download className="size-4" aria-hidden="true" />
                    {language === 'es' ? 'Descargar' : 'Download'}
                  </a>
                )}
              </span>
            </li>
          ))}
        </ul>
      )}
      {open && (
        <div className="mt-4 space-y-3">
          {staged.length > 0 && (
            <ul className="divide-y divide-border rounded-control border border-dashed border-border px-3">
              {staged.map((file, index) => (
                <li
                  key={`${file.name}-${index}`}
                  className="flex flex-wrap items-center justify-between gap-3 py-2 text-sm"
                >
                  <span className="flex min-w-0 items-center gap-2">
                    <FileText className="size-4 shrink-0 text-ink/40" aria-hidden="true" />
                    <span className="min-w-0 break-all">{file.name}</span>
                  </span>
                  {confirmRemove === index ? (
                    <span className="flex items-center gap-2">
                      <span className="text-xs text-ink/65">{text('confirmRemoveStaged', language)}</span>
                      <button
                        type="button"
                        className="schd-button-destructive-sm"
                        onClick={() => {
                          setStaged((prev) => prev.filter((_, i) => i !== index));
                          setConfirmRemove(null);
                        }}
                      >
                        <X className="size-4" aria-hidden="true" />
                        {text('remove', language)}
                      </button>
                      <button
                        type="button"
                        className="schd-button-secondary-sm"
                        onClick={() => setConfirmRemove(null)}
                      >
                        {text('cancel', language)}
                      </button>
                    </span>
                  ) : (
                    <button
                      type="button"
                      className="schd-button-tertiary"
                      disabled={submitting}
                      onClick={() => setConfirmRemove(index)}
                    >
                      <X className="size-4" aria-hidden="true" />
                      {text('remove', language)}
                    </button>
                  )}
                </li>
              ))}
            </ul>
          )}
          <div className="flex flex-wrap items-center gap-3">
            <label
              className={`schd-button-secondary ${submitting ? 'cursor-not-allowed opacity-55' : 'cursor-pointer'}`}
              aria-disabled={submitting}
            >
              <UploadCloud className="size-4" aria-hidden="true" />
              {text('attachFiles', language)}
              <input
                className="sr-only"
                type="file"
                accept="application/pdf,image/png,image/jpeg"
                multiple
                disabled={submitting}
                onChange={(event) => {
                  addFiles(event.currentTarget.files);
                  event.currentTarget.value = '';
                }}
              />
            </label>
            {staged.length > 0 && (
              <button
                type="button"
                className="schd-button-primary"
                disabled={submitting}
                onClick={() => void submit()}
              >
                {submitting ? (
                  <LoaderCircle
                    className="size-4 animate-spin motion-reduce:animate-none"
                    aria-hidden="true"
                  />
                ) : (
                  <UploadCloud className="size-4" aria-hidden="true" />
                )}
                {submitting ? text('uploading', language) : text('submitDocuments', language)}
              </button>
            )}
          </div>
        </div>
      )}
    </li>
  );
}

function Documents({
  requests,
  language,
}: {
  requests: ReturnType<typeof useQuery<{ data: DocumentRequest[] }>>;
  language: 'en' | 'es';
}) {
  return (
    <section className="mx-auto max-w-3xl">
      {requests.isLoading ? (
        <div className="grid gap-4">
          <CardSkeleton />
          <CardSkeleton />
        </div>
      ) : requests.isError ? (
        <ErrorState
          error={requests.error}
          language={language}
          retry={() => void requests.refetch()}
        />
      ) : requests.data?.data.length ? (
        <ul className="grid gap-4">
          {requests.data.data.map((request) => (
            <DocumentRequestCard
              key={request.id}
              request={request}
              language={language}
              onChanged={() => requests.refetch()}
            />
          ))}
        </ul>
      ) : (
        <div className="schd-card">
          <Empty icon={FileText} text={text('documentsHint', language)} />
        </div>
      )}
    </section>
  );
}

function Profile({
  profile,
  language,
  updateLanguage,
  savingLanguage,
}: {
  profile: ApplicantProfile;
  language: 'en' | 'es';
  updateLanguage: (language: 'en' | 'es') => Promise<void>;
  savingLanguage: boolean;
}) {
  const initials =
    `${profile.name.firstName[0] ?? ''}${profile.name.lastName[0] ?? ''}`.toUpperCase();
  const queryClient = useQueryClient();
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const uploadPhoto = async (file: File) => {
    setUploadingPhoto(true);
    try {
      await uploadAvatar('/me/avatar', file);
      await queryClient.invalidateQueries({ queryKey: ['my-profile'] });
      toast.success(text('photoUpdated', language));
    } catch (error) {
      toast.error(errorMessage(error, language));
    } finally {
      setUploadingPhoto(false);
    }
  };
  return (
    <div className="mx-auto max-w-3xl space-y-5">
      <section className="schd-card flex items-center gap-4">
        <Avatar
          photoUrl={
            profile.avatarUpdatedAt
              ? `${apiBaseUrl}/me/avatar?v=${encodeURIComponent(profile.avatarUpdatedAt)}`
              : null
          }
          initials={initials}
          size={56}
          editable
          uploading={uploadingPhoto}
          uploadLabel={text('uploadPhoto', language)}
          onSelectFile={(file) => void uploadPhoto(file)}
        />
        <h2 className="text-xl font-semibold break-words">
          {profile.name.firstName} {profile.name.lastName}
        </h2>
      </section>
      <section className="schd-card">
        <h3 className="text-sm font-semibold tracking-wide text-ink/60 uppercase">
          {text('contactInfo', language)}
        </h3>
        <dl className="mt-4 space-y-4">
          <div className="flex items-start gap-3">
            <Mail className="mt-0.5 size-4 shrink-0 text-ink/40" aria-hidden="true" />
            <div className="min-w-0">
              <dt className="text-xs text-ink/55">{text('email', language)}</dt>
              <dd className="font-medium break-words">{profile.email}</dd>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <Phone className="mt-0.5 size-4 shrink-0 text-ink/40" aria-hidden="true" />
            <div className="min-w-0">
              <dt className="text-xs text-ink/55">{text('phone', language)}</dt>
              <dd className="font-medium">{profile.phone || '—'}</dd>
            </div>
          </div>
        </dl>
        <p className="mt-4 text-xs text-ink/55">{text('contactOffice', language)}</p>
      </section>
      <section className="schd-card">
        <h3 className="text-sm font-semibold tracking-wide text-ink/60 uppercase">
          {text('preferences', language)}
        </h3>
        <label className="mt-4 block font-semibold" htmlFor="profile-language">
          {text('language', language)}
        </label>
        <p className="mt-1 text-sm text-ink/65">{text('languageHelp', language)}</p>
        <div className="mt-3 max-w-sm">
          <SearchableSelect
            id="profile-language"
            value={profile.preferredLanguage}
            disabled={savingLanguage}
            onChange={(value) => void updateLanguage(value as 'en' | 'es')}
            options={[
              { value: 'en', label: 'English' },
              { value: 'es', label: 'Español' },
            ]}
          />
        </div>
      </section>
    </div>
  );
}
function SharedResources({
  resources,
  language,
}: {
  resources: ReturnType<typeof useQuery<{ data: SharedResource[] }>>;
  language: 'en' | 'es';
}) {
  return (
    <section className="mx-auto max-w-3xl">
      {resources.isLoading ? (
        <CardSkeleton />
      ) : resources.isError ? (
        <ErrorState
          error={resources.error}
          language={language}
          retry={() => void resources.refetch()}
        />
      ) : resources.data?.data.length ? (
        <ul className="schd-card divide-y divide-border">
          {resources.data.data.map((resource) => (
            <li
              className="flex flex-col gap-3 py-4 first:pt-0 last:pb-0 sm:flex-row sm:items-center sm:justify-between"
              key={resource.id}
            >
              <div className="flex min-w-0 items-start gap-3">
                <span
                  className="grid size-9 shrink-0 place-items-center rounded-control bg-surface-muted text-ink/50"
                  aria-hidden="true"
                >
                  <FileText className="size-4" />
                </span>
                <div className="min-w-0">
                  <h3 className="font-semibold break-words">{resource.title}</h3>
                  <p className="mt-1 text-sm break-all text-ink/55">{resource.fileName}</p>
                </div>
              </div>
              <a
                href={`${apiBaseUrl}/me/shared-resources/${resource.id}/download`}
                className="schd-button-secondary shrink-0"
              >
                <Download className="size-4" aria-hidden="true" />
                {language === 'es' ? 'Descargar' : 'Download'}
              </a>
            </li>
          ))}
        </ul>
      ) : (
        <div className="schd-card">
          <Empty icon={Share2} text={text('sharedResourcesHint', language)} />
        </div>
      )}
    </section>
  );
}
function Empty({ icon: Icon, text }: { icon: typeof BookOpen; text: string }) {
  return (
    <div className="flex flex-col items-center py-8 text-center">
      <span
        className="mb-3 grid size-11 place-items-center rounded-full bg-burgundy-soft text-burgundy"
        aria-hidden="true"
      >
        <Icon className="size-5" />
      </span>
      <p className="max-w-xs text-sm text-ink/65">{text}</p>
    </div>
  );
}
function ErrorState({
  error,
  language,
  retry,
}: {
  error: unknown;
  language: 'en' | 'es';
  retry: () => void;
}) {
  return (
    <div className="schd-error" role="alert">
      <p>{errorMessage(error, language)}</p>
      <button className="schd-button-secondary mt-3" onClick={retry}>
        {text('retry', language)}
      </button>
    </div>
  );
}
